import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  approveInitiativeAreaParticipation,
  getInitiativeAreaParticipations,
  withdrawInitiativeAreaParticipation,
} from '../initiative.service';
import { resultMessage } from '../../../../../service/apiResult';
import InlineAlert from '../../../../components/inlineAlert';
import NotificationSnackbar from '../../../../components/notificationSnackbar';
import { detailPageCardSx as pageCardSx, detailSectionTitleSx } from '../../../../styles/detailStyles';
import {
  confirmationDialogSx,
  confirmationIconSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
  confirmationTextSx,
  confirmationTitleSx,
} from '../../../../styles/dialogStyles';

const headCellSx = { fontWeight: 600, color: '#1C1B1F', fontSize: 14, whiteSpace: 'nowrap' };

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const STATUS = {
  APPROVED: { label: 'Joined', color: 'success' },
  WITHDRAWN: { label: 'Withdrawn', color: 'default' },
};

/**
 * Initiative approval chain (P2-2): the area-based Ecosystem Integrators and whether each takes part in this
 * Initiative. Only areas that joined pass the Initiative on to their DAREs, and through them to farmers. The
 * area integrator decides from its own console; eKutir can join or withdraw here on an area's behalf.
 */
export default function InitiativeAreaParticipation({ initiativeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toWithdraw, setToWithdraw] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!initiativeId) return;
    setLoading(true);
    const res = await getInitiativeAreaParticipations(initiativeId);
    if (res.success) {
      setRows(res.data || []);
    } else {
      setError(resultMessage(res, 'Could not load the area integrators.'));
    }
    setLoading(false);
  }, [initiativeId]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (row) => {
    setSaving(true);
    setError('');
    const res = await approveInitiativeAreaParticipation(initiativeId, row.areaEcosystemId);
    setSaving(false);
    if (res.success) {
      setSuccess(`${row.areaName} joined the Initiative`);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  const closeWithdraw = () => {
    setToWithdraw(null);
    setReason('');
  };

  const withdraw = async () => {
    const row = toWithdraw;
    setToWithdraw(null);
    setSaving(true);
    setError('');
    const res = await withdrawInitiativeAreaParticipation(initiativeId, row.areaEcosystemId, reason.trim());
    setReason('');
    setSaving(false);
    if (res.success) {
      setSuccess(`${row.areaName} withdrawn from the Initiative`);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  return (
    <Box sx={{ ...pageCardSx, mt: 3 }}>
      <Typography sx={{ ...detailSectionTitleSx, mb: 0.5 }}>Area participation</Typography>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', color: '#49454F', mb: 2.5 }}>
        Area-based Ecosystem Integrators that take part in this Initiative. Only areas that joined offer it to
        their DAREs and farmers. Area integrators decide from their own console; use this to decide on an
        area&apos;s behalf.
      </Typography>

      <InlineAlert message={error} onClose={() => setError('')} sx={{ mb: 2 }} />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Area integrator</TableCell>
              <TableCell sx={headCellSx}>Country</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Decided</TableCell>
              <TableCell sx={headCellSx} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: '#49454F', py: 3, textAlign: 'center' }}>
                  {loading ? 'Loading…' : 'No area-based Ecosystem Integrator yet.'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const status = STATUS[row.status];
              return (
                <TableRow key={row.areaEcosystemId}>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.areaName}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#49454F' }}>
                      {[row.integratorName, row.phoneNumber].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.countryName || '-'}
                    {!row.offered && (
                      <Chip size="small" variant="outlined" label="Not offered here" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {status ? (
                      <Chip size="small" color={status.color} label={status.label} />
                    ) : (
                      <Chip size="small" variant="outlined" label="Not joined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{formatDate(row.decidedAt)}</Typography>
                    {row.decidedAt && (
                      <Typography sx={{ fontSize: 12, color: '#49454F' }}>
                        {[row.decidedByName || row.decidedByRole, row.note].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.status === 'APPROVED' ? (
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() => setToWithdraw(row)}
                        sx={{ textTransform: 'none', color: '#DA342E' }}
                        startIcon={<Icon icon="mdi:link-variant-off" />}
                      >
                        Withdraw
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        disabled={saving || !row.offered}
                        onClick={() => approve(row)}
                        sx={{ textTransform: 'none' }}
                        startIcon={<Icon icon="mdi:check-circle-outline" />}
                      >
                        Join
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={Boolean(toWithdraw)} onClose={closeWithdraw}>
        <Box sx={confirmationDialogSx}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box sx={confirmationIconSx}>
              <Icon icon="proicons:alert-triangle" width="36" height="36" color="#C40000" />
            </Box>
            <Typography sx={confirmationTitleSx}>Withdraw area?</Typography>
            <Typography sx={confirmationTextSx}>
              {toWithdraw?.areaName} will no longer offer this Initiative: its DAREs are taken out of it and
              their agents untagged. Farmers already enrolled keep their history.
            </Typography>
            <TextField
              fullWidth
              size="small"
              required
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ pt: 1 }}>
              <Button
                variant="contained"
                disabled={!reason.trim()}
                onClick={withdraw}
                sx={confirmationPrimaryButtonSx}
              >
                Withdraw
              </Button>
              <Button onClick={closeWithdraw} sx={confirmationSecondaryButtonSx}>Cancel</Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={Boolean(success)}
        onClose={() => setSuccess('')}
        message={success}
        severity="success"
        contentSx={{ px: 2.5 }}
      />
    </Box>
  );
}
